import { FC, useMemo, useState } from "react";

import { formatDollars } from "capitalrx-utilities";

import { Button, Buttons, Icon, Menu } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { AccumulationBalanceObject } from "@/api/interfaces/Medical";
import styles from "./BenefitSearchForm.module.scss";

export interface AccumulationsData {
  data: {
    family_accumulated: string;
    family_maximum: string;
    individual_accumulated: string;
    individual_maximum: string;
  };
}

export interface AccumulationsBarChartProps {
  accumulations: AccumulationBalanceObject[];
}

const toNumericString = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  return String(v).replace(/[^0-9.-]/g, "");
};

const formatPercentage = (accumulated: string, maximum: string) => {
  const acc = Number(accumulated);
  const max = Number(maximum);
  if (!isFinite(acc) || !isFinite(max) || max <= 0) return 0;
  return (acc / max) * 100;
};

const getAccumulationSections = (
  accumulations: Record<string, AccumulationsData["data"]>,
  prefix: "INN" | "OON",
) => [
  {
    title: "Individual Deductible",
    accumulated: accumulations[`${prefix} Deductible`]?.individual_accumulated,
    maximum: accumulations[`${prefix} Deductible`]?.individual_maximum,
  },
  {
    title: "Individual Maximum Out-of-Pocket",
    accumulated: accumulations[`${prefix} MOOP`]?.individual_accumulated,
    maximum: accumulations[`${prefix} MOOP`]?.individual_maximum,
  },
  {
    title: "Family Deductible",
    accumulated: accumulations[`${prefix} Deductible`]?.family_accumulated,
    maximum: accumulations[`${prefix} Deductible`]?.family_maximum,
  },
  {
    title: "Family Maximum Out-of-Pocket",
    accumulated: accumulations[`${prefix} MOOP`]?.family_accumulated,
    maximum: accumulations[`${prefix} MOOP`]?.family_maximum,
  },
];

const appliesPharmacyOnly = (applies: string[]) => {
  const a = applies.map((s) => s.toLowerCase());
  return a.includes("pharmacy") && !a.includes("medical");
};

const appliesPharmacyAndMedical = (applies: string[]) => {
  const a = applies.map((s) => s.toLowerCase());
  return a.includes("pharmacy") && a.includes("medical");
};

const appliesMedicalOnly = (applies: string[]) => {
  const a = applies.map((s) => s.toLowerCase());
  return a.includes("medical") && !a.includes("pharmacy");
};

type TypeKey = "Deductible" | "MOOP";

const mapTypeKey = (typeRaw: string): TypeKey | null => {
  const t = (typeRaw || "").toLowerCase();
  if (t === "deductible") return "Deductible";
  if (t === "moop" || t === "maximum_out_of_pocket") return "MOOP";
  return null;
};

const mapNetworkKey = (networkRaw: string): "INN" | "OON" | null => {
  const n = (networkRaw || "").toLowerCase();
  if (n === "out_of_network") return "OON";
  if (n === "inn") return "INN";
  if (n === "in_network" || n.startsWith("in_network")) return "INN";
  return null;
};

const computeInnScore = (applies: string[]) => {
  const a = applies.map((x) => x.toLowerCase());
  const hasMed = a.includes("medical");
  const hasRx = a.includes("pharmacy");
  if (hasMed && hasRx) return 2;
  if (hasRx) return 1;
  return 0;
};

const computeOonScore = (applies: string[]) => {
  const a = applies.map((x) => x.toLowerCase());
  const hasMed = a.includes("medical");
  const hasRx = a.includes("pharmacy");
  if (hasMed && !hasRx) return 2;
  if (hasMed && hasRx) return 1;
  return 0;
};

type Candidate = {
  score: number;
  endTs: number | null;
  item: AccumulationBalanceObject;
};

const parseISOOrNull = (s?: string | null): number | null => {
  if (!s) return null;
  const t = Date.parse(s);
  return isNaN(t) ? null : t;
};

/**
 * Format for component
 * {
 *   "INN Deductible": {individual_accumulated, individual_maximum, family_accumulated, family_maximum},
 *   "INN MOOP":      {...},
 *   "OON Deductible":{...},
 *   "OON MOOP":      {...}
 * }
 *
 * Filter by applies_to:
 * - INN: includes only pharmacy-only OR pharmacy+medical combined
 * - OON: includes only medical-only
 */
const normalizeForView = (
  api: AccumulationBalanceObject[],
): Record<string, AccumulationsData["data"]> => {
  const emptyNode = (): AccumulationsData["data"] => ({
    individual_accumulated: "",
    individual_maximum: "",
    family_accumulated: "",
    family_maximum: "",
  });

  const best: Record<string, Candidate> = {};
  const setIfBetter = (key: string, cand: Candidate) => {
    const prev = best[key];
    if (!prev) {
      best[key] = cand;
      return;
    }
    if (cand.score > prev.score) {
      best[key] = cand;
      return;
    }
    if (cand.score === prev.score) {
      const p = prev.endTs === null ? -Infinity : prev.endTs;
      const c = cand.endTs === null ? -Infinity : cand.endTs;
      if (c > p) best[key] = cand;
    }
  };

  for (const raw of api || []) {
    if (!raw || !raw.type || !raw.network || !Array.isArray(raw.applies_to)) continue;

    const typeKey = mapTypeKey(raw.type);
    if (!typeKey) continue;

    const networkKey = mapNetworkKey(raw.network);
    if (!networkKey) continue;

    const applies = raw.applies_to;

    if (networkKey === "INN") {
      const allow =
        appliesPharmacyOnly(applies) || appliesPharmacyAndMedical(applies);
      if (!allow) continue;

      const score = computeInnScore(applies);
      if (score <= 0) continue;

      setIfBetter(`INN ${typeKey}`, {
        score,
        endTs: parseISOOrNull(raw.end_date),
        item: raw,
      });
    }

    if (networkKey === "OON") {
      const allow = appliesMedicalOnly(applies);
      if (!allow) continue;

      const score = computeOonScore(applies);
      if (score <= 0) continue;

      setIfBetter(`OON ${typeKey}`, {
        score,
        endTs: parseISOOrNull(raw.end_date),
        item: raw,
      });
    }
  }

  const out: Record<string, AccumulationsData["data"]> = {
    "INN Deductible": emptyNode(),
    "INN MOOP": emptyNode(),
    "OON Deductible": emptyNode(),
    "OON MOOP": emptyNode(),
  };

  Object.keys(out).forEach((key) => {
    const cand = best[key];
    if (!cand) return;
    const it = cand.item;
    out[key] = {
      individual_accumulated: toNumericString(it.individual_accumulated),
      individual_maximum: toNumericString(it.individual_maximum),
      family_accumulated: toNumericString(it.family_accumulated),
      family_maximum: toNumericString(it.family_maximum),
    };
  });

  return out;
};

const AccumulationSection: FC<{
  title: string;
  accumulated: string;
  maximum: string;
}> = ({ title, accumulated, maximum }) => {
  const percentage = formatPercentage(accumulated, maximum);
  const isComplete =
    isFinite(Number(accumulated)) &&
    isFinite(Number(maximum)) &&
    Math.abs(Number(accumulated) - Number(maximum)) < 0.01;

  const safeFormatDollars = (value: string | number | null | undefined) => {
    const num = Number(value);
    return isNaN(num) ? "n/a" : formatDollars(num);
  };

  return (
    <Stack spacing="small">
      <Semibold style={{ fontSize: "16px" }}>{title}</Semibold>
      <Body>Member limit is {safeFormatDollars(maximum)}</Body>
      <div className={styles.AccumulationBar} role="progressbar" aria-valuemin={0} aria-valuemax={Number(maximum) || 0} aria-valuenow={Number(accumulated) || 0} aria-label={title}>
        <div className={styles.Filled} style={{ width: `${percentage}%` }} />
        {isComplete && (
          <div className={styles.Check}>
            <Icon name="check-mark" color="white" size="small" />
          </div>
        )}
      </div>
      <Stack spacing="none">
        <Shelf>
          <Semibold>{safeFormatDollars(accumulated)}</Semibold>
          <Shelf.Spacer />
          <Semibold>
            {safeFormatDollars(Number(maximum) - Number(accumulated))}
          </Semibold>
        </Shelf>
        <Shelf>
          <Body>Spent</Body>
          <Shelf.Spacer />
          <Body>Remaining</Body>
        </Shelf>
      </Stack>
    </Stack>
  );
};

export const Accumulations: FC<AccumulationsBarChartProps> = ({ accumulations }) => {
  const [networkSelection, setNetworkSelection] = useState<"inn" | "oon">("inn");
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const toggleMenu = () => setMenuIsOpen((prev) => !prev);
  const closeMenu = () => setMenuIsOpen(false);
  const handleNetworkChange = (value: "inn" | "oon") => {
    setNetworkSelection(value);
    closeMenu();
  };

  const normalized = useMemo(() => normalizeForView(accumulations), [accumulations]);

  const prefix: "INN" | "OON" = networkSelection === "inn" ? "INN" : "OON";
  const sections = getAccumulationSections(normalized, prefix).filter(
    ({ accumulated, maximum }) => Boolean(accumulated) && Boolean(maximum),
  );

  return (
    <Stack spacing="medium-large">
      <Stack spacing="none">
        <Stack alignment="left">
          <Button
            icon="chevron-down"
            iconPosition="right"
            appearance={Buttons.Functional.Tertiary.Small}
            onClick={toggleMenu}
          >
            {networkSelection === "inn" ? "In-Network" : "Out-of-Network"}
          </Button>
        </Stack>
        <Stack>
          <Menu visible={menuIsOpen} onClose={closeMenu}>
            <Menu.Group>
              <Menu.Item type="normal" onClick={() => handleNetworkChange("inn")}>
                In-Network
              </Menu.Item>
              <Menu.Item type="normal" onClick={() => handleNetworkChange("oon")}>
                Out-of-Network
              </Menu.Item>
            </Menu.Group>
            <Menu.Group>
              <Menu.Item onClick={closeMenu}>Close</Menu.Item>
            </Menu.Group>
          </Menu>
        </Stack>
      </Stack>

      {(networkSelection === "inn" || networkSelection === "oon") && (
        <>
          {sections.length > 0 ? (
            sections.map(({ title, accumulated, maximum }) => (
              <AccumulationSection
                key={`${prefix}-${title}`}
                title={title}
                accumulated={accumulated}
                maximum={maximum}
              />
            ))
          ) : (
            <Body>
              No results found for {prefix === "INN" ? "In-Network" : "Out-of-Network"}.
            </Body>
          )}
        </>
      )}
    </Stack>
  );
};
